// Selector de color del editor gráfico: secciones "De tu marca" / "Generales" /
// "Últimos usados", swatch transparente opcional, gotero (EyeDropper API donde
// exista) y "+" que abre el color picker NATIVO del sistema.
//
// Desktop: POPOVER portaleado con position:fixed anclado al trigger (los toolbars
// tienen overflow-x-auto y clipearían un `absolute`). Mobile: BOTTOM-SHEET (mismo
// patrón que los pickers de Atlas: portal + slide-up + safe-area). El contenido es
// el mismo en ambos.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Pipette, Plus } from 'lucide-react';
import { addUltimoColor, getUltimosColores } from '../lib/ultimosColores';
import { useIsDesktop } from '../lib/useIsDesktop';

const PRESETS = [
  '#FFFFFF',
  '#000000',
  '#1644EB', // hex-de-marca-ok: paleta que elige el usuario
  '#F59E0B',
  '#EF4444',
  '#10B981',
  '#A855F7',
  '#EC4899',
  '#0EA5E9',
  '#FACC15',
];

const POPOVER_W = 208;

// EyeDropper API (Chromium: Chrome/Edge/Opera). No tipada en lib.dom por defecto.
const hasEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

/** Deduplica (case-insensitive) y descarta lo que no sea hex de 6 dígitos. */
function dedupeHex(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    if (typeof raw !== 'string') continue;
    let c = raw.trim();
    if (!/^#?[0-9a-fA-F]{6}$/.test(c)) continue;
    if (!c.startsWith('#')) c = `#${c}`;
    const k = c.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

const CHECKER: CSSProperties = {
  backgroundColor: '#fff',
  backgroundImage:
    'linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4),' +
    'linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)',
  backgroundSize: '10px 10px',
  backgroundPosition: '0 0, 5px 5px',
};

/** Tablero de ajedrez + barra roja = "sin relleno" / transparente. */
function TransparentSwatch({ size = 28 }: { size?: number }) {
  return (
    <span className="relative inline-block rounded-md overflow-hidden shrink-0" style={{ width: size, height: size, ...CHECKER }}>
      <span
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to top right, transparent calc(50% - 1px), #DC2626 calc(50% - 1px), #DC2626 calc(50% + 1px), transparent calc(50% + 1px))',
        }}
      />
    </span>
  );
}

interface Props {
  value: string;
  onChange: (color: string) => void;
  /** Paleta de la marca; se muestra arriba como "De tu marca" si tiene items. */
  brandColors?: string[];
  /** Expone un swatch "sin relleno" (transparent). */
  allowTransparent?: boolean;
  /** Dónde anclar el popover respecto del trigger (solo desktop). Default 'left'. */
  align?: 'left' | 'right';
  /** Texto en el trigger en vez del hex (ej. "Relleno", "Borde", "Color"). */
  triggerLabel?: string;
  ariaLabel?: string;
  /** Muestra la paleta embebida en el panel (sin trigger ni popover). Ej: panel Fondo. */
  inline?: boolean;
}

export function ColorPicker({
  value,
  onChange,
  brandColors,
  allowTransparent,
  align = 'left',
  triggerLabel,
  ariaLabel,
  inline,
}: Props) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isTransparent = value === 'transparent';

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left =
      align === 'right'
        ? Math.max(8, r.right - POPOVER_W)
        : Math.min(r.left, window.innerWidth - POPOVER_W - 8);
    setPos({ top: r.bottom + 8, left });
  };

  const toggle = () => {
    if (!open) {
      setRecent(getUltimosColores());
      if (isDesktop) place();
    }
    setOpen((o) => !o);
  };

  // Inline (paleta embebida): cargar "Últimos usados" al montar. En modo popover
  // se cargan al abrir (toggle); acá no hay trigger que dispare esa carga.
  useEffect(() => {
    if (inline) setRecent(getUltimosColores());
  }, [inline]);

  // Desktop: cerrar al clickear afuera y al hacer scroll/resize. En mobile el
  // sheet se cierra con el backdrop / botón "Cerrar".
  useEffect(() => {
    if (!open || !isDesktop) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScrollResize = () => setOpen(false);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open, isDesktop]);

  const pick = (color: string, close = true) => {
    onChange(color);
    if (color !== 'transparent') addUltimoColor(color);
    if (inline) setRecent(getUltimosColores());
    if (close && !inline) setOpen(false);
  };

  const openEyeDropper = async () => {
    const Ctor = (window as unknown as {
      EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> };
    }).EyeDropper;
    if (!Ctor) return;
    try {
      const res = await new Ctor().open();
      if (res?.sRGBHex) pick(res.sRGBHex);
    } catch {
      /* el usuario canceló el gotero */
    }
  };

  const cleanBrand = dedupeHex(brandColors ?? []);
  const isActive = (c: string) => !isTransparent && c.toLowerCase() === (value || '').toLowerCase();

  const swatch = (c: string, keyPrefix: string) => (
    <button
      key={`${keyPrefix}-${c}`}
      type="button"
      onClick={() => pick(c)}
      className={`w-7 h-7 rounded-md transition-transform active:scale-95 md:hover:scale-110 ${
        isActive(c) ? 'ring-2 ring-brand-primary ring-offset-1' : ''
      }`}
      style={{
        backgroundColor: c,
        boxShadow: c.toLowerCase() === '#ffffff' ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : undefined,
      }}
      title={c}
      aria-label={`Color ${c}`}
    />
  );

  const sectionLabel = 'text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5';

  // Contenido compartido por el popover (desktop) y el bottom-sheet (mobile).
  const content = (
    <>
      {cleanBrand.length > 0 && (
        <>
          <div className={sectionLabel}>De tu marca</div>
          <div className="flex flex-wrap gap-1.5 mb-2">{cleanBrand.map((c) => swatch(c, 'brand'))}</div>
          <div className="h-px bg-neutral-200 -mx-2 mb-2" />
        </>
      )}

      <div className={sectionLabel}>Generales</div>
      <div className="grid grid-cols-6 gap-1.5">
        {PRESETS.map((c) => swatch(c, 'preset'))}

        {allowTransparent && (
          <button
            type="button"
            onClick={() => pick('transparent')}
            className={`w-7 h-7 rounded-md transition-transform active:scale-95 md:hover:scale-110 ring-1 ring-black/10 ${
              isTransparent ? 'ring-2 ring-brand-primary ring-offset-1' : ''
            }`}
            title="Sin relleno (transparente)"
            aria-label="Sin relleno (transparente)"
          >
            <TransparentSwatch size={28} />
          </button>
        )}

        {hasEyeDropper && (
          <button
            type="button"
            onClick={openEyeDropper}
            className="w-7 h-7 rounded-md flex items-center justify-center border border-neutral-200 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 transition-colors"
            title="Gotero (elegir un color de la pantalla)"
            aria-label="Gotero"
          >
            <Pipette className="w-3.5 h-3.5" />
          </button>
        )}

        <label
          className="w-7 h-7 rounded-md flex items-center justify-center border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 cursor-pointer relative transition-colors"
          title="Color personalizado"
        >
          <Plus className="w-3.5 h-3.5" />
          <input
            type="color"
            value={/* hex-de-marca-ok: paleta que elige el usuario */ /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#1644EB'}
            onChange={(e) => pick(e.target.value, false)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
      </div>

      {recent.length > 0 && (
        <>
          <div className="h-px bg-neutral-200 -mx-2 my-2" />
          <div className={sectionLabel}>Últimos usados</div>
          <div className="flex flex-wrap gap-1.5">{recent.map((c) => swatch(c, 'recent'))}</div>
        </>
      )}
    </>
  );

  // Inline: la paleta va embebida en el panel (sin trigger ni popover).
  if (inline) {
    return <div>{content}</div>;
  }

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        className={`h-8 ${triggerLabel ? 'pl-2 pr-1.5' : 'pl-1 pr-2'} flex items-center gap-1.5 rounded-md border bg-white transition-colors ${
          open ? 'border-brand-primary/40 ring-2 ring-brand-primary/15' : 'border-black/[0.08] hover:border-black/[0.16]'
        }`}
        aria-label={ariaLabel || 'Elegir color'}
        title={isTransparent ? 'Sin relleno' : value}
      >
        {triggerLabel && <span className="text-[12px] text-neutral-700">{triggerLabel}</span>}
        {isTransparent ? (
          <TransparentSwatch size={22} />
        ) : (
          <span
            className="w-[22px] h-[22px] rounded shrink-0"
            style={{
              backgroundColor: value || '#000000',
              boxShadow: (value || '').toLowerCase() === '#ffffff' ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : undefined,
            }}
          />
        )}
        <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />
      </button>

      {/* Desktop: popover anclado al trigger. */}
      {open &&
        isDesktop &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[60] bg-white border border-neutral-200 rounded-lg shadow-lg p-2"
            style={{ top: pos.top, left: pos.left, width: POPOVER_W }}
          >
            {content}
          </div>,
          document.body,
        )}

      {/* Mobile: bottom-sheet. */}
      {open &&
        !isDesktop &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[200] bg-black/40 animate-fade-in" onClick={() => setOpen(false)} />
            <div
              className="fixed inset-x-0 bottom-0 z-[201] eg-oscuro eg-hoja bg-surface-card rounded-t-2xl shadow-2xl animate-slide-up-sheet"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
            >
              <div className="py-2.5 flex justify-center">
                <div className="w-9 h-[5px] rounded-full bg-black/[0.15]" />
              </div>
              <div className="flex items-center justify-between px-4 pb-2.5 border-b border-neutral-100">
                <button type="button" onClick={() => setOpen(false)} className="text-[15px] text-white/70 active:opacity-60 w-16 text-left">
                  Cerrar
                </button>
                <span className="text-[15px] font-semibold text-brand-black/90">{triggerLabel || 'Color'}</span>
                <span className="w-16" aria-hidden />
              </div>
              <div className="p-4">{content}</div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
